import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { FilterBar } from '@/components/ui/filter-bar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useInvoices } from '@/hooks';
import { usePaginationState } from '@/hooks/usePaginationState';
import { PaginationInfo } from '@/components/PaginationInfo';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { calculatePaginationValues } from '@/lib/pagination';
import { resolveInvoiceColumns } from '@/lib/view-registries/invoice-columns';
import { resolveInvoiceActions, type InvoiceActionContext } from '@/lib/view-registries/invoice-actions';
import type { UserView } from '@/lib/schemas';

interface InvoiceViewRendererProps {
  view: UserView;
}

export function InvoiceViewRenderer({ view }: InvoiceViewRendererProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const config = view.config;

  // Resolve columns and actions from registries
  const columns = resolveInvoiceColumns(config.columns || []);
  const actions = resolveInvoiceActions(config.quick_actions || []);

  // Read sort/search from URL, fallback to config defaults
  const sortBy = searchParams.get('sort') || config.sort?.field || 'created_at';
  const sortOrder = searchParams.get('order') || config.sort?.direction || 'desc';
  const search = searchParams.get('search') || '';

  // Pagination
  const { page, limit, offset, setPage } = usePaginationState(50);

  // Build status param from config filters
  const statusParam = config.filters?.status
    ? config.filters.status.join(',')
    : undefined;

  // Fetch invoices with config-driven params
  const { data, isLoading, error } = useInvoices(
    {
      status: statusParam,
      search: search || undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
      limit,
      offset,
    },
    { keepPreviousData: true }
  );

  const invoices = data?.invoices || [];
  const total = data?.total || 0;
  const paginationValues = calculatePaginationValues(total, page, limit);

  // Action context for registry actions
  const actionCtx: InvoiceActionContext = { navigate };

  // Sort handler
  const handleSort = (column: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (sortBy === column) {
      newParams.set('order', sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      newParams.set('sort', column);
      newParams.set('order', 'desc');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Search handler
  const handleSearchChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.delete('page');
    setSearchParams(newParams, { replace: true });
  };

  const handleClearAll = () => {
    setSearchParams(new URLSearchParams());
  };

  // SortableHeader component
  const SortableHeader = ({
    column,
    label,
    className,
  }: {
    column: string;
    label: string;
    className?: string;
  }) => {
    const isActive = sortBy === column;
    return (
      <TableHead
        className={cn('cursor-pointer select-none hover:bg-muted/50', className)}
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          {label}
          {isActive ? (
            sortOrder === 'asc' ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )
          ) : (
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground/50" />
          )}
        </div>
      </TableHead>
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout title={view.name}>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={view.name}>
      {/* Filter Bar */}
      <FilterBar
        search={search}
        onSearchChange={handleSearchChange}
        placeholder="Search invoices..."
        onClearAll={handleClearAll}
        activeFilterCount={search ? 1 : 0}
      />

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Failed to load invoices: {(error as Error).message}
          </p>
        </div>
      )}

      {/* Table */}
      {!error && (
        <Table containerClassName="rounded-md border">
          <TableHeader>
            <TableRow>
              {columns.map(({ key, def }) =>
                def.sortable && def.sortKey ? (
                  <SortableHeader
                    key={key}
                    column={def.sortKey}
                    label={def.label}
                    className={cn(def.className, def.align === 'right' && 'text-right')}
                  />
                ) : (
                  <TableHead
                    key={key}
                    className={cn(def.className, def.align === 'right' && 'text-right')}
                  >
                    {def.label}
                  </TableHead>
                )
              )}
              {actions.length > 0 && <TableHead className="w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (actions.length > 0 ? 1 : 0)}
                  className="text-center h-32"
                >
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <p>No invoices found</p>
                    {search && (
                      <p className="text-sm mt-1">
                        Try adjusting your search
                      </p>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow
                  key={invoice.id}
                  onClick={() => navigate(`/invoices/${invoice.id}`)}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  {columns.map(({ key, def }) => (
                    <TableCell
                      key={key}
                      className={cn(def.className, def.align === 'right' && 'text-right')}
                    >
                      {def.render(invoice)}
                    </TableCell>
                  ))}
                  {actions.length > 0 && (
                    <TableCell>
                      <div className="flex items-center gap-1 justify-end">
                        {actions
                          .filter(({ def }) => def.visible(invoice))
                          .map(({ key, def }) => {
                            const Icon = def.icon;
                            return (
                              <TooltipProvider key={key}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        def.execute(invoice, actionCtx);
                                      }}
                                    >
                                      <Icon className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>{def.label}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <PaginationInfo
            total={total}
            start={paginationValues.startItem}
            end={paginationValues.endItem}
            itemName="invoices"
          />
          {paginationValues.totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage(page - 1)}
                    className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(5, paginationValues.totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => setPage(pageNum)}
                        isActive={page === pageNum}
                        className="cursor-pointer"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage(page + 1)}
                    className={
                      page >= paginationValues.totalPages
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
